// React
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';

// Third party
import { trackPromise } from 'react-promise-tracker';

// Styles
import styles from './SalesChannelForm.module.scss';

// Material UI
import { LinearProgress } from '@material-ui/core';
import { DataGrid, GridOverlay } from '@material-ui/data-grid';

// Contexts
import { ListingContext } from '../../../contexts/listing';
import { SalesChannelContext } from '../../../contexts/saleschannel';

// Services
import { getListingData as getSalesChannelTabListingData } from '../../../services/sales-channel';

// Hooks
import useMounted from '../../../hooks/useMounted';

// No rows component
function NoRowsOverlay() {
  return (
    <GridOverlay className={styles['no-data']}>
      <div>
        <strong>No matching records found</strong>
      </div>
      <div>Try adding new linked components or adjusting your search by changing or removing filters</div>
    </GridOverlay>
  );
}

// This component
export default function SalesChannelTabDataGrid({ isModal }) {
  // Listing Context
  const {
    endpoint,
    getColumns,
    getRows,
    activeFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortModel,
    setSortModel,
    refreshDataGrid,
    setRefreshDataGrid
  } = useContext(ListingContext);

  // Sales Channel Context
  const {
    currentSalesChannelInfo,
    tabModalSelectedValues,
    setTabModalSelectedValues,
    tabOwnSelectedValues,
    setTabOwnSelectedValues,
    tabPendingRemoved
  } = useContext(SalesChannelContext);

  // If the component is mounted
  const mounted = useMounted();

  // Data (current page only, pagination, sort and filters are server-side)
  const [data, setData] = useState({
    totalRecords: 0,
    data: []
  });

  // Columns
  const [columns, setColumns] = useState([]);

  // Keep reference to the inital passed function (we don't need to watch its changes, just need to invoke)
  const getRowsRef = useRef(getRows);
  const getColumnsRef = useRef(getColumns);

  // Selected rows are kept across pages, the grid itself only knows the rows of the current page
  const selectedRows = isModal ? tabModalSelectedValues : tabOwnSelectedValues;
  const setSelectedRows = isModal ? setTabModalSelectedValues : setTabOwnSelectedValues;

  // Selection of the current page, as sorted string ids to match what the grid emits
  const pageSelectionModel = useMemo(() => {
    const selectedIds = new Set(selectedRows.map((row) => row.id.toString()));
    return data.data
      .map((row) => row.id.toString())
      .filter((id) => selectedIds.has(id))
      .sort((a, b) => a - b);
  }, [data, selectedRows]);

  // Rows pending removal are struck through until saved
  const gridColumns = useMemo(() => {
    if (isModal) {
      return columns;
    }

    const pendingRemovedIds = new Set(tabPendingRemoved.map((row) => row.id.toString()));
    return columns.map((column) => ({
      ...column,
      cellClassName: (params) => pendingRemovedIds.has(params.id.toString()) ? styles['pending-removal'] : ''
    }));
  }, [isModal, columns, tabPendingRemoved]);

  // Page change
  const handlePageChange = (params) => {
    setPage(params.page);
    setRefreshDataGrid(true);
  };

  // Page size change
  const handlePageSizeChange = (params) => {
    setPageSize(params.pageSize);
    setPage(0);
    setRefreshDataGrid(true);
  };

  // Sort change
  const handleSortChange = (params) => {
    if (params.sortModel !== sortModel && params.sortModel.length > 0) {
      setSortModel(params.sortModel);
      setPage(0);
      setRefreshDataGrid(true);
    }
  };

  // Selection change
  const handleSelectionChange = (params) => {
    const pageIds = new Set(data.data.map((row) => row.id.toString()));
    const pageSelectedIds = new Set(params.selectionModel.map((id) => id.toString()));

    setSelectedRows([
      ...selectedRows.filter((row) => !pageIds.has(row.id.toString())),
      ...data.data.filter((row) => pageSelectedIds.has(row.id.toString()))
    ]);
  };

  // Get data
  useEffect(() => {
    if (refreshDataGrid && currentSalesChannelInfo.salesChannelId) {
      let promise = getSalesChannelTabListingData({
        endpoint,
        pageSize,
        getRows: getRowsRef.current,
        page,
        sortModel,
        activeFilters,
        isSalesChannelListing: true,
        salesChannelID: currentSalesChannelInfo.salesChannelId,
        isModal
      });

      trackPromise(
        promise.then((newData) => {
          if (
            mounted.current &&
            newData &&
            newData.data &&
            typeof newData.data === 'object'
          ) {
            setData(newData);
            setColumns((columns) =>
              getColumnsRef.current(newData.data, columns)
            );
            setRefreshDataGrid(false);
          }
        })
      );
    }
  }, [
    endpoint,
    pageSize,
    page,
    sortModel,
    activeFilters,
    refreshDataGrid,
    setRefreshDataGrid,
    mounted,
    currentSalesChannelInfo,
    isModal
  ]);

  // JSX
  return (
    <>
      {refreshDataGrid && <LinearProgress />}
      {!refreshDataGrid && (
        <DataGrid
          columns={gridColumns}
          rows={data.data}
          pagination={true}
          autoHeight={true}
          rowHeight={40}
          disableSelectionOnClick={true}
          disableColumnMenu={true}
          paginationMode='server'
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          page={page}
          onPageChange={handlePageChange}
          rowCount={data.totalRecords || data.data.length}
          rowsPerPageOptions={[25, 50, 100]}
          sortingOrder={['desc', 'asc']}
          sortingMode='server'
          sortModel={sortModel}
          onSortModelChange={handleSortChange}
          selectionModel={pageSelectionModel}
          onSelectionModelChange={handleSelectionChange}
          components={{
            NoRowsOverlay: NoRowsOverlay
          }}
          checkboxSelection={true}
        />
      )}
    </>
  );
}
